import React, { useRef, useEffect, useState } from 'react';
import {
  Result,
  ResultList as ResultListType,
  SearchEngine,
  buildFacet,
  buildNumericFacet,
  buildNumericRange,
  ContextPayload,
  loadAdvancedSearchQueryActions,
  loadContextActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
  BreadcrumbManager,
  FacetOptions,
  NumericFacetOptions
} from '@coveo/headless';
import { ExtendedWindowType, headlessEngine } from '../../engine';
import ResultList from '../../components/result-list';
import { resultTemplatesManager } from './templates';
import Pagination from '../../components/pagination';
import { Sort } from '../../components/sort';
import { sortFields } from '../../controllers/sort-criteria';
import QuerySummary from '../../components/query-summary';
import FacetBreadcrumbs from '../../components/facet-breadcrumbs';
import {
  facets,
  labelsObj,
  resultList,
  fieldsObject,
  defaultQuery,
  facetBreadcrumb,
  resultsPerPage
} from '../../controllers/controller';
import Facet from '../../components/facet';
import DistanceFacet, { DISTANCE_PLACEHOLDERS } from './distance-facet';
import { getAdvancedQueryParts } from '../../../helpers/transform';
import MobileFilters from '../../components/mobile-filters';
import { bindUrlManager } from '../../components/url-manager';
import ToggleSwitch from '../../../react-components/toggle-switch';
import { getStoredGeolocationCountryAndZip } from '../../../helpers/geolocation';
import SecondCall from '../../components/second-call-context';
import { dispatchPersonaViewEvent } from '../../../helpers/personalize';
import DOMPurify from 'dompurify';

// Extended types to support resultsMustMatch
interface ExtendedFacetOptions extends FacetOptions {
  resultsMustMatch?: 'allValues' | 'atLeastOneValue';
}

interface ExtendedNumericFacetOptions extends NumericFacetOptions {
  resultsMustMatch?: 'allValues' | 'atLeastOneValue';
}

const facetsObj = facets.map(facet => {
  if (!facet.range) {
    const options: ExtendedFacetOptions = {
      field: facet.field,
      filterFacetCount: true,
      numberOfValues: 999,
      resultsMustMatch: facet.resultsMustMatch || 'atLeastOneValue'
    };

    return {
      field: facet.field,
      title: facet.title,
      certificationsLabel: facet.certificationsLabel,
      specialtiesLabel: facet.specialtiesLabel,
      controller: buildFacet(headlessEngine as SearchEngine, {
        options
      }),
      customOrder: facet.customOrder,
      resultsMustMatch: facet.resultsMustMatch || 'atLeastOneValue'
    };
  } else {
    const options: ExtendedNumericFacetOptions = {
      field: facet.field,
      generateAutomaticRanges: false,
      sortCriteria: facet.sortCriteria ? facet.sortCriteria : 'ascending',
      resultsMustMatch: facet.resultsMustMatch || 'atLeastOneValue',
      currentValues: facet.rangeValues?.map(value =>
        buildNumericRange({
          start: value.start,
          end: value.end,
          endInclusive: value.endInclusive
        })
      )
    };

    return {
      title: facet.title,
      rangeLabels:
        facet.sortCriteria === 'ascending'
          ? facet.rangeLabels
          : facet.rangeLabels?.reverse(),
      rangeStars: facet.rangeStars,
      controller: buildNumericFacet(headlessEngine as SearchEngine, {
        options
      }),
      customOrder: facet.customOrder,
      resultsMustMatch: facet.resultsMustMatch || 'atLeastOneValue'
    };
  }
});

type CityListingProps = {
  labels: string;
  switchFacet: string;
  renderFacets: string;
  defaultCountryValue: string;
  limitSecondCall: string;
};

type SwitchFacetProps = {
  field: string;
  redirectUrl: string;
  values: Array<{ value: string; label: string; default: boolean }>;
};

type SchemaItem = {
  '@type': string;
  position: number;
  item: {
    '@type': string;
    name: string;
    url: string;
    logo: string;
    contactPoint: {
      '@type': string;
      telephone: string;
      contactType: string;
      areaServed: string;
    };
  };
};

const advancedQuerySearchActionCreator = loadAdvancedSearchQueryActions(
  headlessEngine as SearchEngine
);

function CityListing({
  switchFacet,
  renderFacets,
  defaultCountryValue,
  limitSecondCall
}: CityListingProps) {
  const switchFacetObj = JSON.parse(switchFacet) as SwitchFacetProps;
  const defaultSwitchValue = switchFacetObj.values.find(value => value.default);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const moduleRef = useRef<HTMLDivElement | null>(null);
  const [resultsState, setResultsState] = useState(
    (resultList as ResultListType).state
  );

  const [breadcrumbsState, setBreadcrumbsState] = useState(
    (facetBreadcrumb as BreadcrumbManager).state
  );

  const [secondCall, setSecondCall] = useState(false);

  const [originalResults, setOriginalResults] = useState<Result[]>([]);
  const [filteredResults, setFilteredResults] = useState<Result[]>([]);
  const [originalContext, setOriginalContext] = useState<undefined | string>(
    undefined
  );
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [selectedFacets, setSelectedFacets] = useState<
    {
      facetId: string;
      value: string | { start: number; end: number };
    }[]
  >([]);
  const [promoCards, setPromoCards] = useState('');

  useEffect(() => {
    dispatchPersonaViewEvent();
    if (!secondCall) return;
    setFilteredResults(() => {
      const facets = selectedFacets;
      let newFilteredResults = originalResults;
      facets.forEach(
        (facet: {
          facetId: string;
          value: string | { start: number; end: number };
        }) => {
          newFilteredResults = newFilteredResults.filter((result: Result) => {
            if (
              result.raw[facet.facetId] &&
              Array.isArray(result.raw[facet.facetId])
            ) {
              return (result.raw[facet.facetId] as string[]).includes(
                facet.value as string
              );
            } else {
              return (
                (result.raw[facet.facetId] as number) >=
                  (facet.value as { start: number; end: number }).start &&
                (result.raw[facet.facetId] as number) <=
                  (facet.value as { start: number; end: number }).end
              );
            }
          });
        }
      );

      return newFilteredResults;
    });
  }, [selectedFacets, originalResults]);

  useEffect(() => {
    if (headlessEngine) {
      bindUrlManager(headlessEngine);

      (resultList as ResultListType).subscribe(() => {
        setResultsState((resultList as ResultListType).state);
      });
      (facetBreadcrumb as BreadcrumbManager).subscribe(() =>
        setBreadcrumbsState((facetBreadcrumb as BreadcrumbManager).state)
      );

      if (defaultQuery) {
        const advancedQuerySearchActionCreator =
          loadAdvancedSearchQueryActions(headlessEngine);

        headlessEngine?.dispatch(
          advancedQuerySearchActionCreator.registerAdvancedSearchQueries({
            aq: defaultQuery
          })
        );
      }
    }

    let observer: MutationObserver;

    const targetNode = document.querySelector('.coveo-listing__promo-cards');

    if (targetNode) {
      const targetInnerHtml = targetNode.innerHTML;

      setPromoCards(targetInnerHtml);

      const config = { attributes: true, childList: true, subtree: true };

      const callback = (mutationList: MutationRecord[]) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            setPromoCards(targetNode.innerHTML);
          }
        }
      };

      observer = new MutationObserver(callback);
      observer.observe(targetNode, config);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  const initAdvancedQuery = (value: string) => {
    let newQuery = '';
    const previousQuery = (headlessEngine as SearchEngine).state
      .advancedSearchQueries?.aq;

    if (previousQuery) {
      const parts = getAdvancedQueryParts(previousQuery);
      let newParts = parts.map(part => {
        if (part.includes(`@${switchFacetObj.field}`)) {
          part = `@${switchFacetObj.field} = ${value}`;
          return part.trim();
        }

        return part.trim();
      });

      newParts = newParts.filter((part, index) => {
        const nextPart = newParts[index + 1]
          ? newParts[index + 1].trim()
          : undefined;
        return !(part === 'AND' && (nextPart === '' || nextPart === undefined));
      });

      newQuery = newParts.join(' ');
    } else {
      newQuery = `@${switchFacetObj.field.trim()} = ${value.trim()}`;
    }

    newQuery.toString();
    if (switchFacetObj.values.length > 0) {
      (headlessEngine as SearchEngine)?.dispatch(
        advancedQuerySearchActionCreator.registerAdvancedSearchQueries({
          aq: newQuery
        })
      );
    }
  };

  useEffect(() => {
    if (defaultSwitchValue) {
      initAdvancedQuery(defaultSwitchValue.value);
    }
  }, []);

  const handleSwitchChange = () => {
    if (moduleRef.current) {
      const zipValue = moduleRef.current.querySelector<HTMLInputElement>(
        '.zip-form__zip-input'
      )?.value;
      const countryValue = moduleRef.current.querySelector<HTMLSelectElement>(
        '.zip-form__country-select'
      )?.value;

      const url = switchFacetObj.redirectUrl
        .replace(DISTANCE_PLACEHOLDERS.zipCode, zipValue || '')
        .replace(DISTANCE_PLACEHOLDERS.country, countryValue || '');

      window.location.href = url;
    }
  };

  const renderFilters = (isMobile?: boolean) => {
    return (
      <div className="contractor-listing__facets">
        {facetsObj.map((facet, index) => (
          <Facet
            field={facet.field}
            coveoEngine={headlessEngine as SearchEngine}
            key={`${index}-${facet.title}`}
            controller={facet.controller}
            title={facet.title}
            certificationsLabel={facet.certificationsLabel}
            specialtiesLabel={facet.specialtiesLabel}
            customOrder={facet.customOrder}
            defaultExpanded={isMobile ? false : true}
            rangeLabels={facet.rangeLabels}
            rangeStarLabels={facet.rangeStars}
            resultsMustMatch={facet.resultsMustMatch}
          />
        ))}
      </div>
    );
  };

  const generateItemList = (results: Result[]) => {
    const resultList: SchemaItem[] = [];

    results.forEach((result, index) => {
      const sanitizedName = DOMPurify.sanitize(
        (result.raw[fieldsObject.contractorName] as string) || ''
      );
      const sanitizedUrl = DOMPurify.sanitize(
        (result.raw[fieldsObject.link] as string) || ''
      );
      const sanitizedLogo = DOMPurify.sanitize(
        (result.raw[fieldsObject.image] as string) || ''
      );
      const sanitizedPhone = DOMPurify.sanitize(
        (result.raw[fieldsObject.phone] as string) || ''
      );
      const sanitizedArea = DOMPurify.sanitize(
        (result.raw[fieldsObject.areaServed] as string) || ''
      );

      resultList.push({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Organization',
          name: sanitizedName,
          url: sanitizedUrl,
          logo: sanitizedLogo,
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: sanitizedPhone,
            contactType: 'sales',
            areaServed: sanitizedArea
          }
        }
      });
    });

    return resultList;
  };

  const checkSecondCall = () => {
    if (breadcrumbsState.hasBreadcrumbs) return;

    const totalResults = headlessEngine?.state.pagination?.totalCountFiltered;
    if (totalResults === undefined) return;

    const selectedSort = sortFields.find(
      (sort: {
        title: string;
        criterion: { by: string; field: string; order: string };
      }) => {
        const headlessEng = headlessEngine as SearchEngine;
        if (
          !headlessEng ||
          !headlessEng.state ||
          !headlessEng.state.sortCriteria
        )
          return;
        if (sort.criterion.by === 'relevancy') {
          if (headlessEng?.state.sortCriteria === 'relevancy') {
            return true;
          }
        } else if (sort.criterion.by === 'field') {
          if (
            headlessEng?.state?.sortCriteria.includes(sort.criterion.field) &&
            headlessEng?.state?.sortCriteria.includes(sort.criterion.order)
          ) {
            return true;
          }
        }
        return false;
      }
    );

    const actualContext = (headlessEngine as SearchEngine).state.context
      ?.contextValues.sortingStrategy;

    if (selectedSort && selectedSort?.context?.secondaryThreshold > 0) {
      if (
        selectedSort.context.secondarySortingStrategy &&
        actualContext &&
        actualContext !== selectedSort.context.secondarySortingStrategy &&
        totalResults < selectedSort.context.secondaryThreshold &&
        !secondCall
      ) {
        const { setContext } = loadContextActions(
          headlessEngine as SearchEngine
        );
        setOriginalContext(selectedSort.context.sortingStrategy);
        resultsPerPage?.set(parseInt(limitSecondCall) || 20);
        headlessEngine?.dispatch(
          setContext({
            sortingStrategy: selectedSort.context.secondarySortingStrategy
          } as ContextPayload)
        );
        const { executeSearch } = loadSearchActions(
          headlessEngine as SearchEngine
        );
        const { logInterfaceChange } = loadSearchAnalyticsActions(
          headlessEngine as SearchEngine
        );
        headlessEngine?.dispatch(executeSearch(logInterfaceChange()));
        setSecondCall(true);
      } else if (
        actualContext === selectedSort.context.secondarySortingStrategy
      ) {
        setSecondCall(true);
        setOriginalResults(resultsState.results);
        setFilteredResults(resultsState.results);
      } else {
        setSecondCall(false);
      }
    }
  };

  useEffect(() => {
    if (
      resultsState.firstSearchExecuted &&
      resultsState.searchResponseId &&
      !resultsState.isLoading
    ) {
      checkSecondCall();
      const results = resultsState.results;

      const jsonldScript = document.createElement('script');
      jsonldScript.setAttribute('type', 'application/ld+json');

      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: DOMPurify.sanitize(labelsObj.listName || ''),
        description: DOMPurify.sanitize(labelsObj.listDescription || ''),
        url: window.location.href,
        itemListElement: generateItemList(results)
      };

      const jsonldScripts = document.querySelectorAll(
        'script[type="application/ld+json"]'
      );

      jsonldScripts.forEach(script => {
        if (script.textContent?.includes(`"@type":"ItemList"`)) script.remove();
      });

      jsonldScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(jsonldScript);
    }

    if (resultsState.firstSearchExecuted && !resultsState.isLoading) {
      const searchParams = new URLSearchParams(window.location.search);
      const storedData = getStoredGeolocationCountryAndZip();
      (window as ExtendedWindowType).dataLayer.push({
        event_attributes: {
          sort_by: getSortFieldLabel(),
          zip_code: searchParams.get(DISTANCE_PLACEHOLDERS.zipCode)
            ? searchParams.get(DISTANCE_PLACEHOLDERS.zipCode)
            : storedData?.zip,
          zip_code_country: searchParams.get(DISTANCE_PLACEHOLDERS.country)
            ? searchParams.get(DISTANCE_PLACEHOLDERS.country)
            : storedData?.country,
          distance_filter: searchParams.get('distance'),
          business_type: moduleRef.current?.querySelector(
            '.toggle-switch__button.active'
          )?.textContent
        },
        event: 'results_viewed',
        event_category: 'results page viewed',
        event_action: 'contractor locator',
        event_label: resultsState.hasResults ? 'success' : 'failure'
      });
    }
  }, [resultsState]);

  const getSortFieldLabel = () => {
    return sortFields.find(
      (field: { criterion: { field: string; order: string; by: string } }) => {
        if (headlessEngine?.state.sortCriteria === 'relevancy') {
          return field.criterion.by === 'relevancy';
        }
        return (
          headlessEngine?.state?.sortCriteria?.includes(
            field.criterion.field
          ) &&
          headlessEngine?.state?.sortCriteria?.includes(field.criterion.order)
        );
      }
    ).title;
  };

  const handleClickResult = (e: React.MouseEvent) => {
    if (
      (
        (e.target as HTMLElement)?.parentNode as HTMLElement
      )?.classList.contains('certification-card__image') ||
      (
        (e.target as HTMLElement)?.parentNode as HTMLElement
      )?.classList.contains('link--inline')
    ) {
      const searchParams = new URLSearchParams(window.location.search);
      const storedData = getStoredGeolocationCountryAndZip();
      (window as ExtendedWindowType).dataLayer.push({
        event_attributes: {
          sort_by: getSortFieldLabel(),
          zip_code: searchParams.get(DISTANCE_PLACEHOLDERS.zipCode)
            ? searchParams.get(DISTANCE_PLACEHOLDERS.zipCode)
            : storedData?.zip,
          zip_code_country: searchParams.get(DISTANCE_PLACEHOLDERS.country)
            ? searchParams.get(DISTANCE_PLACEHOLDERS.country)
            : storedData?.country,
          distance_filter: searchParams.get('distance'),
          business_type: moduleRef.current?.querySelector(
            '.toggle-switch__button.active'
          )?.textContent
        },
        event: 'result_click',
        event_category: 'result click',
        event_action: 'contractor locator'
      });
    }
  };

  return (
    <SecondCall.Provider
      value={{
        secondCall,
        limitValue: parseInt(limitSecondCall),
        originalContext,
        setOriginalContext,
        originalResults,
        filteredResults,
        setFilteredResults,
        selectedPage,
        setSelectedPage,
        selectedFacets,
        setSelectedFacets
      }}
    >
      <div className="contractor-listing__wrapper row" ref={moduleRef}>
        <div className="contractor-listing__left-wrapper">
          <DistanceFacet
            defaultCountryValue={defaultCountryValue}
            shouldSearch={true}
          />
          <ToggleSwitch
            onValueChange={handleSwitchChange}
            values={switchFacetObj.values}
            defaultValue={defaultSwitchValue?.value}
          />
          <div
            className="contractor-listing__mobile-filters"
            ref={containerRef}
          >
            {renderFacets !== 'false' && (
              <MobileFilters
                label={labelsObj.mobileFilterBy}
                containerRef={containerRef}
                refinementLayer={true}
              >
                {renderFilters(true)}
              </MobileFilters>
            )}
          </div>
          <div className="contractor-listing__desktop-filters">
            {renderFacets !== 'false' && renderFilters()}
          </div>
          <div
            className="contractor-listing__promo-cards contractor-listing__promo-cards--desktop"
            dangerouslySetInnerHTML={{ __html: promoCards }}
          />
        </div>
        <div
          className="contractor-listing__right-wrapper"
          onMouseDown={handleClickResult}
        >
          <div className="contractor-listing__history-wrapper">
            <div className="contractor-listing__summary">
              <QuerySummary />
              <FacetBreadcrumbs
                clearLabel={labelsObj.clearAll}
                extraClasses="contractor-listing__breadcrumbs--mobile"
              />
            </div>
            <Sort
              sortByLabel={labelsObj.sortBy}
              extraClasses={`contractor-listing__sort ${
                sortFields.length === 1 ? 'disabled' : ''
              }`}
            />
          </div>
          <FacetBreadcrumbs
            clearLabel={labelsObj.clearAll}
            extraClasses="contractor-listing__breadcrumbs--desktop"
          />
          <ResultList
            controller={resultList as ResultListType}
            className="contractor-listing__results"
            resultTemplatesManager={resultTemplatesManager}
            preventFirstCall={true}
          />
          <Pagination />
          <div
            className="contractor-listing__promo-cards contractor-listing__promo-cards--mobile"
            dangerouslySetInnerHTML={{ __html: promoCards }}
          />
        </div>
      </div>
    </SecondCall.Provider>
  );
}

export default CityListing;
